import {BaseComponent} from '../base/component.base.js';
import template from './page-list.template.html';
import styles from './page-list.styles.css';
import {getLocale, t, tpl} from '../../i18n/i18n.js';
import {readAndClearJustPublishedPaths, displayName, pageUrlPath, liveUrl, hasLiveVersion, hasPendingEdit} from '../../core/page/page.core.js';
import {PageSort, PublishStage, RenderMode, StagedOperation} from '../../core/vocabulary.js';

const COLORS = ['blue', 'purple', 'emerald', 'orange', 'rose', 'amber', 'indigo', 'cyan', 'pink', 'slate'];

const COLOR_MAP = {
    blue: '#2563eb', purple: '#9333ea', emerald: '#059669', orange: '#ea580c',
    rose: '#e11d48', amber: '#d97706', indigo: '#4f46e5', cyan: '#0891b2',
    pink: '#db2777', slate: '#475569'
};

/** Sort control options in menu order: a `PageSort` value and the i18n key of its label. */
const SORT_OPTIONS = Object.freeze([
    [PageSort.DATE_DESC, 'pageList.sortDateDesc'], [PageSort.DATE_ASC, 'pageList.sortDateAsc'],
    [PageSort.TITLE_ASC, 'pageList.sortTitleAsc'], [PageSort.TITLE_DESC, 'pageList.sortTitleDesc'],
    [PageSort.TEMPLATE, 'pageList.sortTemplate'],
]);

export class PageListComponent extends BaseComponent {
    /**
     * Ordered "we're working on it" stages. The backend SSE only emits
     * queued/running/completed — within the long-running "running" window we
     * rotate through these client-side so users see meaningful progress instead
     * of a frozen bar. Percent climbs monotonically; the last stage holds at
     * 90% until the completed event snaps to 100%.
     */
    static #publishRunningStages = [
        {key: 'pageList.stageOptimizing', icon: 'fa-image',       percent: 35},
        {key: 'pageList.stageGenerating', icon: 'fa-code',        percent: 55},
        {key: 'pageList.stageCreatingPr', icon: 'fa-code-branch', percent: 75},
        {key: 'pageList.stageMerging',    icon: 'fa-code-merge',  percent: 90},
    ];
    static #publishRotationIntervalMs = 2500;

    #currentPage = 0;
    #totalElements = 0;
    #totalPages = 0;
    #filterTemplate = '';
    #templates = [];
    #templateColors = new Map();
    #pages = [];
    #hasStagedDrafts = false;
    #stagedLayoutConflictPaths = new Set();
    #stagedLayoutMovedPaths = new Set();
    #layoutConflictCheckFailed = false;
    #loadingMode = 'loading';
    #publishProgressPercent = 0;
    #publishProgressText = '';
    #publishProgressIcon = 'fa-hourglass-half';
    #publishStage = null;
    #publishStageRotatorTimer = null;
    #publishStageRotationIndex = 0;
    #domain = null;
    #sortBy = (typeof localStorage !== 'undefined' && localStorage.getItem('pageList.sortBy')) || PageSort.DATE_DESC;
    #search = '';
    #searchTimer = null;
    #loadFailed = false;
    /** Index of the filter option the arrow keys point at while the list is open, or -1. */
    #activeOption = -1;

    /** Delay between the last keystroke in the search box and the request it triggers. */
    static SEARCH_DEBOUNCE_MS = 250;

    #onOutsideClick = (e) => {
        const dropdown = this.$('#template-filter');
        if (dropdown && !e.composedPath().includes(dropdown)) {
            this.#setFilterOpen(false);
        }
        this.#closeMenus();
    };

    #onKeydown = (e) => {
        if (e.key !== 'Escape') return;
        const open = this.shadowRoot?.querySelector('.btn-more[aria-expanded="true"]');
        this.#closeMenus();
        // Escape inside an open menu hands focus back to the button that opened it.
        if (open && e.composedPath().some((node) => node?.classList?.contains('row-menu'))) open.focus();
    };

    #onLocaleChange = () => {
        const allOption = this.$('#template-filter-options')?.querySelector('[data-value=""]');
        if (allOption) allOption.textContent = t('pageList.filterAll');
        if (!this.#filterTemplate) {
            const display = this.$('#template-filter-display');
            if (display) display.textContent = t('pageList.filterAll');
        }
        if (this.#loadFailed) {
            this.loadFailed = true;
        } else if (this.#pages.length > 0) {
            this.pages = this.#pages;
        }
        const infoSpan = this.$('#page-info');
        if (infoSpan) infoSpan.textContent = tpl('pageList.paginationInfo', {
            current: this.#totalPages > 0 ? this.#currentPage + 1 : 0,
            total: this.#totalPages,
            elements: this.#totalElements,
        });
        this.#syncPublishAllState();
    };

    connectedCallback() {
        this.loadTemplate(template, styles);
        document.addEventListener('locale-change', this.#onLocaleChange);
        document.addEventListener('click', this.#onOutsideClick);
        document.addEventListener('keydown', this.#onKeydown);

        const dropdown = this.$('#template-filter');
        const optionsList = this.$('#template-filter-options');

        dropdown.addEventListener('click', () => {
            this.#setFilterOpen(dropdown.getAttribute('aria-expanded') !== 'true');
        });

        dropdown.addEventListener('keydown', (e) => this.#onFilterKeydown(e));

        optionsList.addEventListener('click', (e) => {
            const option = e.target.closest('.custom-select-option');
            if (!option) return;
            e.stopPropagation();
            this.#chooseFilter(option.dataset.value);
        });

        const search = this.$('#page-search');
        search?.addEventListener('input', () => {
            clearTimeout(this.#searchTimer);
            this.#searchTimer = setTimeout(() => {
                this.#search = search.value;
                this.#currentPage = 0;
                this.#requestPages();
            }, PageListComponent.SEARCH_DEBOUNCE_MS);
        });

        const sortSelect = this.$('#sort-by');
        if (sortSelect) {
            sortSelect.replaceChildren(...SORT_OPTIONS.map(([value, key]) => {
                const option = document.createElement('option');
                option.value = value;
                option.dataset.i18n = key;
                option.textContent = t(key);
                return option;
            }));
            sortSelect.value = this.#sortBy;
            sortSelect.addEventListener('change', () => {
                this.#sortBy = sortSelect.value;
                try { localStorage.setItem('pageList.sortBy', this.#sortBy); } catch { /* private mode */ }
                this.#currentPage = 0;
                this.#requestPages();
            });
        }

        this.$('#prev-btn').addEventListener('click', () => {
            if (this.#currentPage > 0) {
                this.#currentPage--;
                this.#requestPages();
            }
        });

        this.$('#next-btn').addEventListener('click', () => {
            this.#currentPage++;
            this.#requestPages();
        });

        this.$('#publish-all-btn').addEventListener('click', () => {
            this.emit('page-publish-staged', {});
        });

        // Reloading the list runs the layout check again: a failed check is not kept.
        this.$('#retry-layout-check').addEventListener('click', () => this.#requestPages());
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('locale-change', this.#onLocaleChange);
        document.removeEventListener('click', this.#onOutsideClick);
        document.removeEventListener('keydown', this.#onKeydown);
        clearTimeout(this.#searchTimer);
        this.#stopPublishStageRotator();
    }

    /** The sort order the list shows, restored from the last visit. */
    get sortBy() {
        return this.#sortBy;
    }

    #requestPages() {
        this.emit('pages-request', {
            template: this.#filterTemplate,
            page: this.#currentPage,
            sort: this.#sortBy,
            search: this.#search,
        });
    }

    /** @returns {HTMLElement[]} the options of the template filter, in list order */
    #filterOptions() {
        return [...(this.$('#template-filter-options')?.querySelectorAll('.custom-select-option') ?? [])];
    }

    /**
     * Opens or closes the template filter. Opening points the arrow keys at the chosen option.
     * @param {boolean} open
     */
    #setFilterOpen(open) {
        const dropdown = this.$('#template-filter');
        dropdown.setAttribute('aria-expanded', String(open));
        const options = this.#filterOptions();
        this.#setActiveOption(open ? Math.max(0, options.findIndex((option) => option.dataset.value === this.#filterTemplate)) : -1);
    }

    /** Marks one filter option as the arrow keys' target and names it on the combobox. */
    #setActiveOption(index) {
        const dropdown = this.$('#template-filter');
        const options = this.#filterOptions();
        this.#activeOption = index >= 0 && index < options.length ? index : -1;
        options.forEach((option, i) => option.classList.toggle('is-active', i === this.#activeOption));
        const active = options[this.#activeOption];
        if (active) {
            dropdown.setAttribute('aria-activedescendant', active.id);
            active.scrollIntoView?.({block: 'nearest'});
        } else {
            dropdown.removeAttribute('aria-activedescendant');
        }
    }

    /**
     * Keyboard control of the template filter, following the combobox pattern: Enter, Space
     * and Alt+ArrowDown open it; the arrow keys, Home and End move through the options; Enter
     * or Space chooses the marked option; Escape and Tab close it.
     * @param {KeyboardEvent} e
     */
    #onFilterKeydown(e) {
        const open = this.$('#template-filter').getAttribute('aria-expanded') === 'true';
        const last = this.#filterOptions().length - 1;
        const move = {
            ArrowDown: Math.min(last, this.#activeOption + 1),
            ArrowUp: Math.max(0, this.#activeOption - 1),
            Home: 0,
            End: last,
        };
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const active = this.#filterOptions()[this.#activeOption];
            if (open && active) {
                this.#chooseFilter(active.dataset.value);
            } else {
                this.#setFilterOpen(!open);
            }
        } else if (e.key in move) {
            e.preventDefault();
            if (open) {
                this.#setActiveOption(move[e.key]);
            } else {
                this.#setFilterOpen(true);
            }
        } else if (e.key === 'Escape' || e.key === 'Tab') {
            this.#setFilterOpen(false);
        }
    }

    /**
     * Applies a template filter from the dropdown and closes it. Choosing the filter that is
     * already applied requests nothing.
     * @param {string} value
     */
    #chooseFilter(value) {
        this.#setFilterOpen(false);
        if (value === this.#filterTemplate) return;
        this.#filterTemplate = value;
        this.#currentPage = 0;
        this.#filterOptions().forEach(opt => {
            opt.classList.toggle('selected', opt.dataset.value === value);
            opt.setAttribute('aria-selected', opt.dataset.value === value ? 'true' : 'false');
        });
        this.#updateFilterDisplay(value);
        this.#requestPages();
    }

    /** @param {import('../../shell/types/user.types.js').TemplateSummary[]} templates */
    set templates(templates) {
        this.#templates = templates || [];
        const optionsList = this.$('#template-filter-options');
        if (!optionsList) return;

        this.#templateColors.clear();
        this.#templates.forEach((tmpl, i) => {
            this.#templateColors.set(tmpl.name, COLORS[i % COLORS.length]);
        });

        while (optionsList.firstChild) optionsList.removeChild(optionsList.firstChild);

        const allOpt = document.createElement('li');
        allOpt.className = 'custom-select-option' + (!this.#filterTemplate ? ' selected' : '');
        allOpt.id = 'template-filter-option-0';
        allOpt.dataset.value = '';
        allOpt.setAttribute('role', 'option');
        allOpt.setAttribute('aria-selected', !this.#filterTemplate ? 'true' : 'false');
        allOpt.textContent = t('pageList.filterAll');
        optionsList.appendChild(allOpt);

        for (const [index, tmpl] of this.#templates.filter(tmpl => tmpl.mode !== RenderMode.INTERNAL).entries()) {
            const color = this.#templateColors.get(tmpl.name);
            const li = document.createElement('li');
            li.className = 'custom-select-option' + (tmpl.name === this.#filterTemplate ? ' selected' : '');
            li.id = `template-filter-option-${index + 1}`;
            li.dataset.value = tmpl.name;
            li.setAttribute('role', 'option');
            li.setAttribute('aria-selected', tmpl.name === this.#filterTemplate ? 'true' : 'false');
            const swatch = document.createElement('span');
            swatch.className = 'color-swatch';
            swatch.style.background = COLOR_MAP[color] || '#6b7280';
            li.appendChild(swatch);
            li.appendChild(document.createTextNode(displayName(tmpl.name)));
            optionsList.appendChild(li);
        }

        this.#updateFilterDisplay(this.#filterTemplate);

        // Re-render pages to apply correct colors if they were already loaded
        if (this.#pages.length > 0) {
            this.pages = this.#pages;
        }
    }

    #updateFilterDisplay(value) {
        const display = this.$('#template-filter-display');
        if (!display) return;
        while (display.firstChild) display.removeChild(display.firstChild);
        if (value) {
            const color = this.#templateColors.get(value);
            const swatch = document.createElement('span');
            swatch.className = 'color-swatch';
            swatch.style.background = COLOR_MAP[color] || '#6b7280';
            display.appendChild(swatch);
            display.appendChild(document.createTextNode(displayName(value)));
        } else {
            display.textContent = t('pageList.filterAll');
        }
    }

    get templates() {
        return this.#templates;
    }

    /** @param {boolean} value */
    set loading(value) {
        const overlay = this.$('#loading-overlay');
        const loadingText = this.$('#loading-text');
        const progress = this.$('#publish-progress');
        if (loadingText) {
            loadingText.hidden = this.#loadingMode === 'publishing';
            loadingText.textContent = this.#loadingMode === 'deleting'
                ? t('pageList.deleting')
                : t('pageList.loadingOverlay');
        }
        if (progress) {
            progress.hidden = !value || this.#loadingMode !== 'publishing';
        }
        this.#syncPublishProgressUi();
        if (overlay) {
            // For initial content load, show inline skeletons instead of the full overlay —
            // it feels faster and lets the user anticipate the final layout.
            const useSkeleton = value && this.#loadingMode === 'loading';
            overlay.hidden = !value || useSkeleton;
            if (useSkeleton) {
                this.#renderSkeletonRows();
            }
        }
    }

    #renderSkeletonRows(count = 4) {
        const container = this.$('#pages-container');
        if (!container) return;
        container.innerHTML = '';
        const list = document.createElement('div');
        list.className = 'page-list';
        for (let i = 0; i < count; i++) {
            const row = document.createElement('div');
            row.className = 'skeleton-row';
            const avatar = document.createElement('div');
            avatar.className = 'skeleton-shape skeleton-avatar';
            const text = document.createElement('div');
            text.className = 'skeleton-text-block';
            const line1 = document.createElement('div');
            line1.className = 'skeleton-shape skeleton-line-1';
            const line2 = document.createElement('div');
            line2.className = 'skeleton-shape skeleton-line-2';
            text.appendChild(line1);
            text.appendChild(line2);
            const action = document.createElement('div');
            action.className = 'skeleton-shape skeleton-action';
            row.appendChild(avatar);
            row.appendChild(text);
            row.appendChild(action);
            list.appendChild(row);
        }
        container.appendChild(list);
    }

    /** @param {'loading' | 'publishing' | 'deleting'} value */
    set loadingMode(value) {
        this.#loadingMode = value === 'publishing'
            ? 'publishing'
            : value === 'deleting'
                ? 'deleting'
                : 'loading';
        const loadingText = this.$('#loading-text');
        const progress = this.$('#publish-progress');
        if (loadingText) {
            loadingText.hidden = this.#loadingMode === 'publishing';
            loadingText.textContent = this.#loadingMode === 'deleting'
                ? t('pageList.deleting')
                : t('pageList.loadingOverlay');
        }
        if (this.#loadingMode !== 'publishing') {
            this.#stopPublishStageRotator();
            this.#publishProgressPercent = 0;
            this.#publishProgressText = t('pageList.preparingPublish');
            this.#publishProgressIcon = 'fa-hourglass-half';
            this.#publishStage = null;
            if (progress) progress.classList.remove('is-success', 'is-failed');
        }
        if (progress) {
            progress.hidden = this.#loadingMode !== 'publishing';
        }
        this.#syncPublishProgressUi();
    }

    /**
     * Stage-driven publish progress. Callers send a `PublishStage` (`'queued'`,
     * `'running'`, `'completed'`, `'failed'`) or `null`. The component owns the percent,
     * label, icon, and rotation: the running stage cycles through 4 sub-stages
     * (optimizing → generating → creating PR → merging) every ~2.5s so the bar
     * doesn't feel frozen during the long PR creation window.
     *
     * @param {PublishStage|null|undefined} value
     */
    set publishStage(value) {
        this.#stopPublishStageRotator();
        const block = this.$('#publish-progress');
        if (block) {
            block.classList.remove('is-success', 'is-failed');
        }

        if (value === PublishStage.QUEUED) {
            this.#publishStage = PublishStage.QUEUED;
            this.#publishProgressPercent = 18;
            this.#publishProgressText = t('pageList.stageQueued');
            this.#publishProgressIcon = 'fa-hourglass-half';
        } else if (value === PublishStage.RUNNING) {
            this.#publishStage = PublishStage.RUNNING;
            this.#publishStageRotationIndex = 0;
            const initial = PageListComponent.#publishRunningStages[0];
            this.#publishProgressPercent = initial.percent;
            this.#publishProgressText = t(initial.key);
            this.#publishProgressIcon = initial.icon;
            this.#startPublishStageRotator();
        } else if (value === PublishStage.COMPLETED) {
            this.#publishStage = PublishStage.COMPLETED;
            this.#publishProgressPercent = 100;
            this.#publishProgressText = t('pageList.stageCompleted');
            this.#publishProgressIcon = 'fa-circle-check';
            if (block) {
                // Force reflow so the burst animation retriggers when is-success was already set.
                void block.offsetWidth;
                block.classList.add('is-success');
            }
        } else if (value === PublishStage.FAILED) {
            this.#publishStage = PublishStage.FAILED;
            this.#publishProgressPercent = 100;
            this.#publishProgressText = t('pageList.stageFailed');
            this.#publishProgressIcon = 'fa-triangle-exclamation';
            if (block) {
                block.classList.add('is-failed');
            }
        } else {
            this.#publishStage = null;
            this.#publishProgressPercent = 0;
            this.#publishProgressText = '';
        }
        this.#syncPublishProgressUi();
    }

    #startPublishStageRotator() {
        this.#publishStageRotatorTimer = setInterval(() => {
            const next = this.#publishStageRotationIndex + 1;
            if (next >= PageListComponent.#publishRunningStages.length) {
                // Hold on the last "merging" stage until the completed event arrives.
                this.#stopPublishStageRotator();
                return;
            }
            this.#publishStageRotationIndex = next;
            const stage = PageListComponent.#publishRunningStages[next];
            this.#publishProgressPercent = stage.percent;
            this.#publishProgressText = t(stage.key);
            this.#publishProgressIcon = stage.icon;
            this.#syncPublishProgressUi();
        }, PageListComponent.#publishRotationIntervalMs);
    }

    #stopPublishStageRotator() {
        if (this.#publishStageRotatorTimer) {
            clearInterval(this.#publishStageRotatorTimer);
            this.#publishStageRotatorTimer = null;
        }
    }

    /** @param {Object} info */
    set pagination(info) {
        const {totalElements, totalPages, currentPage} = info;
        this.#currentPage = currentPage;
        this.#totalElements = totalElements;
        this.#totalPages = totalPages;

        const infoSpan = this.$('#page-info');
        if (infoSpan) {
            infoSpan.textContent = tpl('pageList.paginationInfo', {
                current: totalPages > 0 ? currentPage + 1 : 0,
                total: totalPages,
                elements: totalElements,
            });
        }

        const prevBtn = this.$('#prev-btn');
        if (prevBtn) prevBtn.disabled = currentPage === 0;

        const nextBtn = this.$('#next-btn');
        if (nextBtn) nextBtn.disabled = totalPages === 0 || currentPage === totalPages - 1;

        const container = this.$('#pagination-container');
        if (container) container.hidden = totalPages <= 1;
    }

    /** @param {import('../../shell/types/user.types.js').PageSummary[]} pages */
    set pages(pages) {
        this.#pages = pages || [];
        this.#loadFailed = false;
        this.#renderPages();
    }

    /**
     * Shows that the pages could not be loaded, with a button that asks for them again.
     * Loaded pages replace the message.
     * @param {boolean} value
     */
    set loadFailed(value) {
        this.#loadFailed = Boolean(value);
        if (!this.#loadFailed) return;
        const container = this.$('#pages-container');
        if (!container) return;
        container.replaceChildren(this.#emptyState({
            titleKey: 'pageList.loadFailedTitle',
            bodyKey: 'global.loadFailedBody',
            action: this.#ctaButton('fa-rotate-right', 'global.retry', () => this.#requestPages()),
        }));
    }

    /**
     * @param {{titleKey: string, bodyKey: string, action: HTMLElement}} options
     * @returns {HTMLElement} an empty state whose call to action is `action`
     */
    #emptyState({titleKey, bodyKey, action}) {
        const empty = document.createElement('empty-state');
        const title = document.createElement('span');
        title.slot = 'title';
        title.textContent = t(titleKey);
        action.slot = 'cta';
        empty.append(title, document.createTextNode(t(bodyKey)), action);
        return empty;
    }

    /** A primary call-to-action button with a leading icon. */
    #ctaButton(icon, labelKey, onClick) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn-primary';
        const iconEl = document.createElement('i');
        iconEl.className = `fa-solid ${icon}`;
        iconEl.setAttribute('aria-hidden', 'true');
        button.append(iconEl, ` ${t(labelKey)}`);
        button.addEventListener('click', onClick);
        return button;
    }

    /** Clears the search and the template filter and asks for the first page again. */
    #clearFilters() {
        clearTimeout(this.#searchTimer);
        const search = this.$('#page-search');
        if (search) search.value = '';
        this.#search = '';
        this.#currentPage = 0;
        if (this.#filterTemplate) {
            this.#chooseFilter('');
        } else {
            this.#requestPages();
        }
    }

    /**
     * Snapshot of paths currently staged (not yet published). Used by the
     * event-adapter before a publish-all so it can pulse the rows that
     * actually went live afterwards.
     * @returns {string[]}
     */
    get stagedPagePaths() {
        return this.#pages
            .filter((page) => page && page.published === false && typeof page.path === 'string')
            .map((page) => page.path);
    }

    #renderPages() {
        const container = this.$('#pages-container');
        if (!container) return;
        container.innerHTML = '';
        this.#syncPublishAllState();

        if (this.#pages.length === 0) {
            if (this.#search.trim() || this.#filterTemplate) {
                container.appendChild(this.#emptyState({
                    titleKey: 'pageList.emptyFilteredTitle',
                    bodyKey: 'pageList.emptyFiltered',
                    action: this.#ctaButton('fa-list', 'pageList.clearFilters', () => this.#clearFilters()),
                }));
                return;
            }
            const cta = document.createElement('a');
            cta.href = '#/';
            cta.className = 'btn btn-primary';
            const ctaIcon = document.createElement('i');
            ctaIcon.className = 'fa-solid fa-plus';
            ctaIcon.setAttribute('aria-hidden', 'true');
            cta.append(ctaIcon, ' ' + t('pageList.emptyCta'));
            container.appendChild(this.#emptyState({titleKey: 'pageList.emptyTitle', bodyKey: 'pageList.empty', action: cta}));
            return;
        }

        // Sorting is done server-side (consistent across pagination); we render whatever
        // order the API returned, only re-pinning drafts / auto-moved to the top.
        const sorted = [...this.#pages].sort((a, b) => this.#sortPriority(a) - this.#sortPriority(b));
        const pulsePaths = new Set(readAndClearJustPublishedPaths());
        let rowIndex = 0;
        for (const page of sorted) {
            container.appendChild(this.#createRow(page, pulsePaths.has(page.path), rowIndex));
            rowIndex++;
        }
    }

    /**
     * @param {import('../../shell/types/user.types.js').PageSummary} page
     * @param {boolean} justPublished
     * @param {number} rowIndex
     */
    #createRow(page, justPublished, rowIndex) {
        const row = document.createElement('div');
        const color = this.#templateColors.get(page.template) || COLORS[0];
        const isStaged = page.published === false;
        const pageTemplate = this.#templates.find(tmpl => tmpl.name === page.template);
        const isFragment = pageTemplate?.mode === RenderMode.INDEX_FRAGMENT;
        const isHomepage = pageTemplate?.homepage === true;
        const hasLayoutConflict = this.#isStagedConflict(page);
        const hasAutoMoved = !isStaged && this.#isAutoMoved(page);
        row.className = `page-row theme-${color}${isStaged ? ' staged' : ''}${hasLayoutConflict ? ' layout-conflict' : ''}${hasAutoMoved ? ' position-moved' : ''}${justPublished ? ' just-published' : ''}`;
        row.style.setProperty('--row-reveal-delay', `${Math.min(rowIndex * 60, 600)}ms`);

        const date = new Date(page.createdAt).toLocaleDateString(getLocale(), {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });

        const safeThumbnailUrl = this.#safeThumbnailUrl(page.thumbnailUrl);
        const hasThumbnail = safeThumbnailUrl !== null;
        const iconInner = hasThumbnail
            ? `<img class="page-thumbnail" src="${this.#esc(safeThumbnailUrl)}" alt="" loading="lazy" />`
            : `<i class="page-type-icon fa-solid ${isFragment ? 'fa-puzzle-piece' : 'fa-file-lines'}" aria-hidden="true"></i>`;
        const status = isStaged
            ? `<span class="draft-badge">${hasLayoutConflict ? t('pageList.badgeLayoutOverlap') : (page.operation === StagedOperation.EDIT ? t('pageList.badgePendingEdit') : t('pageList.badgeDraft'))}</span>`
            : `<span class="page-status"><span class="status-dot"></span>${t('pageList.statusLive')}</span>`;
        const urlPath = isFragment ? '' : `<span class="page-url">${this.#esc(pageUrlPath(page.path))}</span>`;
        const homepageBadge = isHomepage ? `<span class="homepage-badge">${this.#esc(t('pageList.homepageBadge'))}</span>` : '';

        row.innerHTML = `
            <button type="button" class="page-main">
                <span class="page-icon-wrapper${hasThumbnail ? ' has-thumbnail' : ''}" aria-hidden="true">
                    ${iconInner}
                </span>
                <span class="page-details">
                    <span class="page-title" title="${this.#esc(page.title)}">${this.#esc(page.title)}</span>
                    <span class="page-meta">
                        ${status}
                        ${homepageBadge}
                        ${urlPath}
                        <span class="template-badge">${this.#esc(displayName(page.template))}</span>
                        <span class="page-date">${this.#esc(date)}</span>
                        ${hasAutoMoved ? `<span class="moved-badge">${t('pageList.badgePositionMoved')}</span>` : ''}
                    </span>
                </span>
            </button>
            <div class="page-actions">
                <button type="button" class="btn btn-edit"><i class="fa-solid fa-pen-to-square" aria-hidden="true"></i> ${this.#esc(t('pageList.edit'))}</button>
                <div class="row-menu-wrap">
                    <button type="button" class="btn btn-more" aria-haspopup="menu" aria-expanded="false"
                            aria-label="${this.#esc(t('pageList.moreActions'))}" title="${this.#esc(t('pageList.moreActions'))}">
                        <i class="fa-solid fa-ellipsis" aria-hidden="true"></i>
                    </button>
                    <div class="row-menu" role="menu" hidden></div>
                </div>
            </div>
        `;

        const emitEdit = () => this.emit('page-edit', {pagePath: page.path, templateName: page.template, live: hasLiveVersion(page), pending: hasPendingEdit(page)});
        row.querySelector('.page-main').addEventListener('click', emitEdit);
        row.querySelector('.btn-edit').addEventListener('click', emitEdit);

        const menu = row.querySelector('.row-menu');
        const moreBtn = row.querySelector('.btn-more');
        moreBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const open = menu.hidden;
            this.#closeMenus();
            menu.hidden = !open;
            moreBtn.setAttribute('aria-expanded', String(open));
            if (open) menu.querySelector('.menu-item:not(:disabled)')?.focus();
        });
        menu.addEventListener('keydown', (e) => this.#onMenuKeydown(e, menu));

        const live = hasLiveVersion(page)
            ? liveUrl(this.#domain, isFragment ? 'index.html' : (page.sourcePath || page.path))
            : null;
        if (live) {
            const viewLive = document.createElement('a');
            viewLive.className = 'menu-item btn-view-live';
            viewLive.setAttribute('role', 'menuitem');
            viewLive.href = live;
            viewLive.target = '_blank';
            viewLive.rel = 'noopener noreferrer';
            viewLive.innerHTML = `<i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i> ${this.#esc(t('pageList.viewLive'))}`;
            menu.appendChild(viewLive);
        }

        if (isStaged) {
            const publishBtn = document.createElement('button');
            publishBtn.type = 'button';
            publishBtn.setAttribute('role', 'menuitem');
            publishBtn.className = 'menu-item btn-publish-one';
            publishBtn.innerHTML = `<i class="fa-solid fa-cloud-arrow-up" aria-hidden="true"></i> ${this.#esc(t('pageList.publishOne'))}`;
            const blockedReason = hasLayoutConflict || this.#layoutConflictCheckFailed
                ? t(this.#layoutConflictCheckFailed ? 'pageList.conflictCheckFailedPublish' : 'pageList.resolveBeforePublish')
                : null;
            if (blockedReason) {
                // aria-disabled keeps the item focusable and in the arrow-key order, so keyboard
                // and screen-reader users reach it and hear why it is blocked.
                const reason = document.createElement('span');
                reason.id = `publish-blocked-${rowIndex}`;
                reason.hidden = true;
                reason.textContent = blockedReason;
                publishBtn.setAttribute('aria-disabled', 'true');
                publishBtn.setAttribute('aria-describedby', reason.id);
                publishBtn.title = blockedReason;
                menu.appendChild(reason);
            }
            publishBtn.addEventListener('click', (e) => {
                // A blocked item ignores activation and keeps its menu open, as a disabled one would.
                if (blockedReason) {
                    e.stopPropagation();
                    return;
                }
                this.#closeMenus();
                this.emit('page-publish-staged', {path: page.targetPath, title: page.title});
            });
            menu.appendChild(publishBtn);

            const removeBtn = document.createElement('button');
            removeBtn.type = 'button';
            removeBtn.setAttribute('role', 'menuitem');
            const isEdit = page.operation === StagedOperation.EDIT;
            removeBtn.className = 'menu-item btn-discard';
            removeBtn.innerHTML = `<i class="fa-solid ${isEdit ? 'fa-rotate-left' : 'fa-xmark'}" aria-hidden="true"></i> ${this.#esc(t(isEdit ? 'pageList.undoEdit' : 'pageList.discard'))}`;
            removeBtn.addEventListener('click', () => {
                this.#closeMenus();
                this.emit('page-discard', {stagedId: page.stagedId, operation: page.operation, title: page.title, path: page.path});
            });
            menu.appendChild(removeBtn);
        } else if (!isHomepage) {
            // The homepage page can never be deleted (the API refuses it); edit it instead.
            const removeBtn = document.createElement('button');
            removeBtn.type = 'button';
            removeBtn.setAttribute('role', 'menuitem');
            removeBtn.className = 'menu-item btn-delete';
            removeBtn.innerHTML = `<i class="fa-solid fa-trash-can" aria-hidden="true"></i> ${this.#esc(t('pageList.delete'))}`;
            removeBtn.addEventListener('click', () => {
                this.#closeMenus();
                this.emit('page-delete', {
                    pagePath: page.path,
                    templateName: page.template,
                    title: page.title,
                });
            });
            menu.appendChild(removeBtn);
        }

        return row;
    }

    /**
     * Arrow keys, Home and End move focus between the items of an open row menu.
     * @param {KeyboardEvent} e
     * @param {HTMLElement} menu
     */
    #onMenuKeydown(e, menu) {
        const items = [...menu.querySelectorAll('.menu-item:not(:disabled)')];
        const current = items.indexOf(this.shadowRoot.activeElement);
        const target = {
            ArrowDown: (current + 1) % items.length,
            ArrowUp: (current - 1 + items.length) % items.length,
            Home: 0,
            End: items.length - 1,
        }[e.key];
        if (target === undefined) return;
        e.preventDefault();
        items[target].focus();
    }

    #closeMenus() {
        this.shadowRoot?.querySelectorAll('.row-menu').forEach(menu => { menu.hidden = true; });
        this.shadowRoot?.querySelectorAll('.btn-more').forEach(btn => btn.setAttribute('aria-expanded', 'false'));
    }

    /** @param {string|null} value The client's site domain, used for "view live" links */
    set domain(value) {
        this.#domain = typeof value === 'string' && value.trim() ? value.trim() : null;
        if (this.#pages.length > 0) this.pages = this.#pages;
    }

    /** @param {boolean} value */
    set hasStagedDrafts(value) {
        this.#hasStagedDrafts = Boolean(value);
        this.#syncPublishAllState();
    }

    /** @param {string[] | Set<string>} paths */
    set stagedLayoutConflictPaths(paths) {
        if (Array.isArray(paths)) {
            this.#stagedLayoutConflictPaths = new Set(paths.filter(Boolean));
        } else if (paths instanceof Set) {
            this.#stagedLayoutConflictPaths = new Set([...paths].filter(Boolean));
        } else {
            this.#stagedLayoutConflictPaths = new Set();
        }
        this.#syncPublishAllState();
        if (this.#pages.length > 0) {
            this.pages = this.#pages;
        }
    }

    /** @param {string[] | Set<string>} paths */
    set stagedLayoutMovedPaths(paths) {
        if (Array.isArray(paths)) {
            this.#stagedLayoutMovedPaths = new Set(paths.filter(Boolean));
        } else if (paths instanceof Set) {
            this.#stagedLayoutMovedPaths = new Set([...paths].filter(Boolean));
        } else {
            this.#stagedLayoutMovedPaths = new Set();
        }
        if (this.#pages.length > 0) {
            this.pages = this.#pages;
        }
    }

    /** @param {boolean} value */
    set layoutConflictCheckFailed(value) {
        this.#layoutConflictCheckFailed = Boolean(value);
        this.#syncPublishAllState();
    }

    #sortPriority(page) {
        if (page.published === false) return 0;
        if (this.#isAutoMoved(page)) return 1;
        return 2;
    }

    #isStagedConflict(page) {
        const candidates = [page.path, page.targetPath, page.sourcePath]
            .filter((value) => typeof value === 'string' && value.length > 0);
        return candidates.some((path) => this.#stagedLayoutConflictPaths.has(path));
    }

    #isAutoMoved(page) {
        const candidates = [page.path, page.targetPath, page.sourcePath]
            .filter((value) => typeof value === 'string' && value.length > 0);
        return candidates.some((path) => this.#stagedLayoutMovedPaths.has(path));
    }

    #syncPublishAllState() {
        const publishAllBtn = this.$('#publish-all-btn');
        const warning = this.$('#layout-conflict-warning');
        const hasConflicts = this.#stagedLayoutConflictPaths.size > 0;
        const shouldBlockPublish = hasConflicts || this.#layoutConflictCheckFailed;

        const publishBar = this.$('#publish-bar');
        if (publishBar) publishBar.hidden = !this.#hasStagedDrafts;
        if (publishAllBtn) {
            publishAllBtn.hidden = !this.#hasStagedDrafts;
            publishAllBtn.disabled = !publishAllBtn.hidden && shouldBlockPublish;
            publishAllBtn.title = publishAllBtn.disabled
                ? (this.#layoutConflictCheckFailed
                    ? t('pageList.conflictCheckFailedPublish')
                    : t('pageList.resolveBeforePublish'))
                : '';
        }
        if (warning) {
            warning.hidden = !this.#hasStagedDrafts || !shouldBlockPublish;
            warning.textContent = this.#layoutConflictCheckFailed
                ? t('pageList.conflictCheckFailed')
                : t('pageList.conflictWarning');
        }
        const retry = this.$('#retry-layout-check');
        if (retry) {
            // Hiding the focused retry button would drop keyboard focus to the page body, so it
            // moves to the publish button, or to the conflict warning while publishing is blocked.
            const retryHadFocus = this.shadowRoot.activeElement === retry;
            retry.hidden = !this.#hasStagedDrafts || !this.#layoutConflictCheckFailed;
            if (retry.hidden && retryHadFocus) {
                if (publishAllBtn && !publishAllBtn.hidden && !publishAllBtn.disabled) publishAllBtn.focus();
                else if (warning && !warning.hidden) warning.focus();
            }
        }
    }

    #syncPublishProgressUi() {
        const bar = this.$('#publish-progress-bar');
        const text = this.$('#publish-progress-text');
        const icon = this.$('#publish-progress-icon');
        if (bar) {
            bar.style.width = `${this.#publishProgressPercent}%`;
        }
        if (text) {
            text.textContent = this.#publishProgressText || t('pageList.preparingPublish');
        }
        if (icon && this.#publishProgressIcon) {
            // Full overwrite avoids accumulating fa-* icon classes across stage swaps.
            icon.className = `publish-progress-icon fa-solid ${this.#publishProgressIcon}`;
        }
    }

    #esc = (s) => {
        if (!s) return '';
        return String(s)
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    /**
     * Returns the thumbnailUrl if it uses a safe scheme (https:, data:image/, or a
     * same-origin absolute path). Returns null otherwise — javascript:, file:, ftp:,
     * and any other scheme is rejected so a malicious server cannot leak XSS into
     * an <img src=>. Falling back to null causes the letter avatar to render.
     * @param {unknown} url
     * @returns {string | null}
     */
    #safeThumbnailUrl(url) {
        if (typeof url !== 'string' || url.length === 0) {
            return null;
        }
        if (url.startsWith('https://') || url.startsWith('data:image/') || url.startsWith('/')) {
            return url;
        }
        return null;
    }

}

customElements.define('page-list', PageListComponent);
